import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Phone, MapPin } from "lucide-react";
import { SocialLinks } from "../SocialLinks/SocialLinks";
import { QuickMessageLinks } from "../QuickMessageLinks/QuickMessageLinks";
import { SALON_PHONE_DISPLAY, SALON_PHONE_TEL } from "../../data/contact";
import { trackBookClick, trackEvent, trackPricesClick } from "../../analytics";

const ADDRESS = "al. Armii Krajowej 6c/4 1 piętro, 50-541 Wrocław";

const Footer: React.FC = (): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const scrollTo = (id: string): void => {
    if (id === "pricesId") {
      trackPricesClick("footer");
    }
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const navLinks = [
    { text: t("prices"), id: "pricesId" },
    { text: t("reviews"), id: "reviewsId" },
    { text: t("map"), id: "howToFindId" },
  ];

  return (
    <footer className="bg-footer text-white">
      <div className="mx-auto grid max-w-6xl gap-8 px-6 py-10 text-center md:grid-cols-3 md:gap-10 md:py-14 md:text-left">
        <div>
          <div className="mb-3 flex items-center justify-center gap-3 md:justify-start">
            <img
              src="/logo.png?v=2"
              alt="Best Groom Studio logo"
              width={125}
              height={160}
              className="h-10 w-auto brightness-0 invert"
            />
            <span
              className="text-lg font-semibold"
              style={{ fontFamily: "Recoleta, serif" }}
            >
              BEST GROOM STUDIO
            </span>
          </div>
          <p className="mb-4 text-sm leading-relaxed text-white/70">
            {t("header.bringingBest")}
          </p>
          <SocialLinks
            className="flex items-center justify-center gap-3 md:justify-start"
            linkClassName="inline-flex size-10 items-center justify-center rounded-full border border-white/15 text-white/80 transition-colors hover:border-mint hover:bg-white/5 hover:text-mint"
          />
        </div>

        <nav
          className="flex flex-col items-center gap-4 md:items-start"
          aria-label="Footer navigation"
        >
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 md:flex-col md:items-start md:gap-3">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => {
                  scrollTo(link.id);
                }}
                className="text-sm text-white/80 transition-colors hover:text-mint"
              >
                {link.text}
              </button>
            ))}
          </div>
          <Link
            to="/appointments"
            className="w-full max-w-xs rounded-full bg-primary px-6 py-2.5 text-sm font-medium text-white shadow-lg shadow-primary/25 transition-colors hover:bg-primary-hover md:w-auto"
            onClick={() => {
              trackBookClick("footer");
            }}
          >
            {t("header.bookAppointment")}
          </Link>
        </nav>

        <div className="flex flex-col items-center gap-3 md:items-start">
          <a
            href="https://www.google.com/maps/place/BEST+GROOM+STUDIO+Nina+Vorona/@51.083752,17.038425,19z"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 text-sm text-white/80 transition-colors hover:text-mint"
          >
            <MapPin size={18} aria-hidden="true" />
            {ADDRESS}
          </a>
          <a
            href={SALON_PHONE_TEL}
            className="inline-flex items-center gap-2 text-sm text-white/80 transition-colors hover:text-mint"
            onClick={() => {
              trackEvent("click_contact", { channel: "call", place: "footer" });
            }}
          >
            <Phone size={18} aria-hidden="true" />
            {SALON_PHONE_DISPLAY}
          </a>
          <QuickMessageLinks
            place="footer"
            variant="dark"
            showCall={false}
            className="mt-1"
          />
        </div>
      </div>

      <div className="border-t border-white/10 px-6 py-5 text-center text-sm text-white/60">
        © {new Date().getFullYear()} Best Groom Studio · {t("footer.rights")}
      </div>
    </footer>
  );
};

export default Footer;
